import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";
import { toast } from "sonner";

export const useDeleteReservationItem = () => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfileQuery();

  return useMutation({
    mutationFn: async (itemId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !profile) throw new Error("Usuario no autenticado");

      // Get the item first
      const { data: item, error: itemError } = await supabase
        .from('reservation_items')
        .select('id, category_id')
        .eq('id', itemId)
        .single();

      if (itemError || !item) {
        console.error('Error fetching item:', itemError);
        throw new Error("Elemento no encontrado");
      }

      // Get the category to find gym_id
      const { data: category, error: categoryError } = await supabase
        .from('reservation_categories')
        .select('gym_id')
        .eq('id', item.category_id)
        .single();

      if (categoryError || !category) {
        console.error('Error fetching category:', categoryError);
        throw new Error("Categoría no encontrada");
      }

      const gymId = category.gym_id;
      const { data: gymManager } = await supabase
        .from('gym_managers')
        .select('*')
        .eq('manager_id', user.id)
        .eq('gym_id', gymId)
        .single();

      if (!gymManager && profile.role !== 'admin') {
        throw new Error("No tienes permisos para gestionar este gimnasio");
      }

      // Soft delete - set is_active to false instead of deleting
      const { error } = await supabase
        .from('reservation_items')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId);

      if (error) throw error;
      return { itemId, categoryId: item.category_id };
    },
    onSuccess: (data) => {
      // Invalidate the specific category items query
      queryClient.invalidateQueries({ 
        queryKey: ['reservation-items', data.categoryId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['reservation-items'] 
      });
      toast.success("Elemento eliminado exitosamente");
    },
    onError: (error) => {
      toast.error(error.message || "Error al eliminar el elemento");
    },
  });
};